import { INITIAL_INPAGE_ROW_COUNT } from "../types/constants";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const EXPANDED_PAGE_SIZE = 100;
const ROW_HEIGHT = 36;
const HEADER_HEIGHT = 38;
const MIN_VISIBLE_ROWS = 4;
const VIEWPORT_BOTTOM_MARGIN = 24;
const METRICS_HEIGHT_CORRECTION = 12;
const IN_PAGE_HEIGHT_ADJUSTMENT = 20;

const measureHeights = (refs) => {
  const {
    fullscreenContainerRef,
    toolbarRef,
    metricsPanelRef,
    paginationRef,
  } = refs;
  const card = fullscreenContainerRef?.current;
  if (!card) return null;
  const cardRect = card.getBoundingClientRect();
  const viewportAvailable =
    window.innerHeight - cardRect.top - VIEWPORT_BOTTOM_MARGIN;
  if (viewportAvailable <= 0) return null;

  const cardContent = card.querySelector(".MuiCardContent-root");
  const cardStyle = cardContent ? window.getComputedStyle(cardContent) : null;
  const cardPadding = cardStyle
    ? parseFloat(cardStyle.paddingTop) + parseFloat(cardStyle.paddingBottom)
    : 0;
  const toolbarHeight = toolbarRef?.current?.offsetHeight ?? 0;
  const toolbarMargin = toolbarRef?.current
    ? parseFloat(window.getComputedStyle(toolbarRef.current).marginBottom) || 0
    : 0;
  const metricsHeight = metricsPanelRef?.current?.offsetHeight ?? 0;
  const metricsEl = metricsPanelRef?.current?.firstElementChild;
  const metricsMargin = metricsEl
    ? parseFloat(window.getComputedStyle(metricsEl).marginBottom) || 0
    : 0;
  const metricsSpace = Math.max(
    0,
    metricsHeight + metricsMargin - METRICS_HEIGHT_CORRECTION
  );
  const paginationHeight = paginationRef?.current?.offsetHeight ?? 0;

  const used =
    cardPadding +
    toolbarHeight +
    toolbarMargin +
    metricsSpace +
    paginationHeight;
  const availableForGrid = viewportAvailable - used;
  const rowCount = Math.max(
    MIN_VISIBLE_ROWS,
    Math.floor((availableForGrid - HEADER_HEIGHT) / ROW_HEIGHT)
  );
  const gridHeight =
    HEADER_HEIGHT + rowCount * ROW_HEIGHT + IN_PAGE_HEIGHT_ADJUSTMENT;

  return { rowCount, gridHeight };
};

export const usePagination = ({
  filteredRows,
  viewMode,
  searchQuery,
  metricsCollapsed,
  metricsPanelRef,
  fullscreenContainerRef,
  containerRef,
  toolbarRef,
  paginationRef,
}) => {
  const initialGridHeight =
    HEADER_HEIGHT + INITIAL_INPAGE_ROW_COUNT * ROW_HEIGHT;
  const [visibleRowCount, setVisibleRowCount] = useState(
    INITIAL_INPAGE_ROW_COUNT
  );
  const [inPageGridHeight, setInPageGridHeight] = useState(initialGridHeight);
  const [startRowIndex, setStartRowIndex] = useState(0);
  const [scrollTargetOnExpand, setScrollTargetOnExpand] = useState(null);
  const isExpanded = viewMode === "fullpage" || viewMode === "fullscreen";

  const prevIsExpandedRef = useRef(isExpanded);
  const isTransitioningRef = useRef(false);
  const savedStartRowIndexRef = useRef(null);
  const prevSearchQueryRef = useRef(searchQuery);
  const preSnapStartRowIndexRef = useRef(startRowIndex);

  const refs = useMemo(
    () => ({
      fullscreenContainerRef,
      toolbarRef,
      metricsPanelRef,
      paginationRef,
    }),
    [fullscreenContainerRef, toolbarRef, metricsPanelRef, paginationRef]
  );

  const effectivePageSize = isExpanded
    ? EXPANDED_PAGE_SIZE
    : visibleRowCount > 0
    ? visibleRowCount
    : INITIAL_INPAGE_ROW_COUNT;

  useEffect(() => {
    if (!isExpanded || prevIsExpandedRef.current) {
      preSnapStartRowIndexRef.current = startRowIndex;
    }
  }, [startRowIndex, isExpanded]);

  useEffect(() => {
    if (prevIsExpandedRef.current === isExpanded) return;
    isTransitioningRef.current = true;
    if (isExpanded) {
      const originalIndex = preSnapStartRowIndexRef.current;
      const snappedIndex =
        Math.floor(startRowIndex / EXPANDED_PAGE_SIZE) * EXPANDED_PAGE_SIZE;
      const relativeScrollTarget = originalIndex - snappedIndex;
      if (relativeScrollTarget > 0) {
        setScrollTargetOnExpand(relativeScrollTarget);
      }
      if (snappedIndex !== startRowIndex) {
        setStartRowIndex(snappedIndex);
      }
    }
    prevIsExpandedRef.current = isExpanded;
    const timeoutId = setTimeout(() => {
      isTransitioningRef.current = false;
    }, 50);
    return () => clearTimeout(timeoutId);
    // Only trigger on view-mode transitions; startRowIndex is read for snap
    // calculation but must not cause re-runs during normal pagination.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  const clearScrollTarget = useCallback(() => {
    setScrollTargetOnExpand(null);
  }, []);

  useEffect(() => {
    if (scrollTargetOnExpand === null || !isExpanded) return undefined;
    const virtualScroller = containerRef.current?.querySelector(
      ".MuiDataGrid-virtualScroller"
    );
    if (!virtualScroller) return undefined;
    const firstRow = virtualScroller.querySelector(".MuiDataGrid-row");
    const rowHeight = firstRow?.offsetHeight || ROW_HEIGHT;
    const targetScrollTop = scrollTargetOnExpand * rowHeight;
    const observer = new MutationObserver(() => {
      const maxScrollTop =
        virtualScroller.scrollHeight - virtualScroller.clientHeight;
      if (
        maxScrollTop > 0 &&
        (virtualScroller.scrollHeight >=
          targetScrollTop + virtualScroller.clientHeight ||
          maxScrollTop <= targetScrollTop)
      ) {
        const scrollTo = Math.min(targetScrollTop, maxScrollTop);
        virtualScroller.scrollTo({ top: scrollTo, behavior: "smooth" });
        clearScrollTarget();
        observer.disconnect();
      }
    });
    observer.observe(virtualScroller, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });
    return () => observer.disconnect();
  }, [isExpanded, scrollTargetOnExpand, containerRef, clearScrollTarget]);

  useEffect(() => {
    if (searchQuery === prevSearchQueryRef.current) return;
    const wasSearching = prevSearchQueryRef.current.trim().length > 0;
    const isSearching = searchQuery.trim().length > 0;
    if (!wasSearching && isSearching) {
      savedStartRowIndexRef.current = startRowIndex;
      setStartRowIndex(0);
    } else if (
      wasSearching &&
      !isSearching &&
      savedStartRowIndexRef.current !== null
    ) {
      setStartRowIndex(
        Math.min(
          savedStartRowIndexRef.current,
          Math.max(0, filteredRows.length - 1)
        )
      );
      savedStartRowIndexRef.current = null;
    } else if (isSearching) {
      setStartRowIndex(0);
    }
    prevSearchQueryRef.current = searchQuery;
    // Only trigger on search query changes; startRowIndex and filteredRows.length
    // are read to save/restore position but must not cause re-runs during pagination.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    if (filteredRows.length > 0 && startRowIndex >= filteredRows.length) {
      setStartRowIndex(0);
    }
  }, [filteredRows.length, startRowIndex]);

  const updateInPageHeight = useCallback(() => {
    if (viewMode === "fullscreen") return;
    const result = measureHeights(refs);
    if (result) {
      setVisibleRowCount(result.rowCount);
      setInPageGridHeight(result.gridHeight);
    }
  }, [viewMode, refs]);

  useLayoutEffect(() => {
    if (viewMode === "fullscreen") {
      setInPageGridHeight(null);
      return;
    }
    updateInPageHeight();
  }, [viewMode, metricsCollapsed, filteredRows.length, updateInPageHeight]);

  useEffect(() => {
    if (viewMode === "fullscreen") return;
    let metricsResizeObserver;
    if (metricsPanelRef?.current && typeof ResizeObserver !== "undefined") {
      metricsResizeObserver = new ResizeObserver(updateInPageHeight);
      metricsResizeObserver.observe(metricsPanelRef.current);
    }
    window.addEventListener("resize", updateInPageHeight);
    return () => {
      metricsResizeObserver?.disconnect();
      window.removeEventListener("resize", updateInPageHeight);
    };
  }, [isExpanded, metricsPanelRef, updateInPageHeight, viewMode]);

  const paginationModel = useMemo(
    () => ({
      pageSize: effectivePageSize,
      page: isExpanded ? Math.floor(startRowIndex / effectivePageSize) : 0,
    }),
    [startRowIndex, effectivePageSize, isExpanded]
  );

  // This is intentionally a no op
  const handlePaginationModelChange = useCallback(() => {}, []);

  const displayedRows = useMemo(
    () => filteredRows.slice(startRowIndex, startRowIndex + effectivePageSize),
    [filteredRows, startRowIndex, effectivePageSize]
  );

  const handlePrevPage = useCallback(() => {
    setStartRowIndex((prev) => Math.max(0, prev - effectivePageSize));
  }, [effectivePageSize]);

  const handleNextPage = useCallback(() => {
    setStartRowIndex((prev) => {
      const newIndex = prev + effectivePageSize;
      return newIndex >= filteredRows.length ? prev : newIndex;
    });
  }, [effectivePageSize, filteredRows.length]);

  const totalRows = filteredRows.length;
  const startRow = totalRows > 0 ? startRowIndex + 1 : 0;
  const endRow = Math.min(startRowIndex + effectivePageSize, totalRows);

  return {
    paginationModel,
    visibleRowCount,
    inPageGridHeight,
    startRowIndex,
    displayedRows,
    handlePaginationModelChange,
    setVisibleRowCount,
    setStartRowIndex,
    handlePrevPage,
    handleNextPage,
    effectivePageSize,
    totalRows,
    startRow,
    endRow,
  };
};
